import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { COUNTRIES, COUNTRY_BY_SLUG } from "../lib/countries";
import { searchActivity, fetchCoupons } from "../lib/api";
import { track } from "../lib/analytics";
import {
  MagnifyingGlass,
  ArrowSquareOut,
  Copy,
  WarningCircle,
  Sparkle,
  Calendar,
  Users,
  Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTIVITIES_BY_COUNTRY = {
  vietnam: [
    "Vinwonders Nha Trang",
    "Vinwonders Phu Quoc",
    "Sun World Ba Na Hills (Golden Bridge)",
    "Halong Bay luxury cruise",
    "Halong Bay seaplane tour",
    "Cu Chi Tunnels day tour",
    "Mekong Delta day tour",
    "Hoi An lantern boat ride",
    "Trang An / Tam Coc boat tour",
    "Sapa terraced fields trek",
    "Cat Ba island hopping",
    "Hanoi street food walking tour",
    "Hue imperial city tour",
    "My Son sanctuary",
    "Ho Chi Minh City Wartime tour",
    "Phong Nha caves expedition",
    "Mui Ne sand dunes 4x4",
  ],
  thailand: [
    "Phi Phi Islands day tour",
    "Safari World Bangkok",
    "Siam Park City Bangkok",
    "Maya Bay speedboat tour",
    "James Bond Island Phang Nga",
    "Elephant sanctuary Chiang Mai (ethical)",
    "Bangkok Grand Palace + Wat Pho",
    "Chao Phraya dinner cruise",
    "Floating market Damnoen Saduak",
    "Ayutthaya temples + lunch",
    "Doi Suthep + Chiang Mai night safari",
    "Krabi 7-island sunset tour",
    "Koh Samui Ang Thong marine park",
    "Tiger Park Phuket",
    "Songkran water festival Bangkok",
    "Muay Thai live fight night",
  ],
  bali: [
    "Tegallalang rice terraces",
    "Mount Batur sunrise trek",
    "Waterbom Bali waterpark",
    "Uluwatu Temple + Kecak fire dance",
    "Nusa Penida day tour (Kelingking)",
    "Ubud Monkey Forest",
    "Tanah Lot temple sunset",
    "Bali swing + Instagram tour",
    "White-water rafting Ayung river",
    "Gili Trawangan snorkeling",
    "Balinese cooking class",
    "ATV quad bike jungle ride",
    "Sekumpul waterfalls trek",
    "Lempuyang Temple of Heaven gates",
  ],
  japan: [
    "Universal Studios Japan (Osaka)",
    "Tokyo Disneyland",
    "Tokyo DisneySea",
    "Mt Fuji + Hakone day tour",
    "Shibuya Sky observatory",
    "TeamLab Planets Tokyo",
    "Tokyo Skytree",
    "Sumo morning practice viewing",
    "Robot Restaurant Shinjuku show",
    "Kyoto golden pavilion + Arashiyama bamboo grove",
    "Nara Park deer feeding",
    "Hiroshima + Miyajima day trip",
    "Sapporo snow festival",
    "Sushi-making class Tokyo",
    "Onsen day pass (Hakone / Beppu)",
    "Kimono rental + Gion walking tour",
  ],
  maldives: [
    "Snorkeling with manta rays",
    "Sandbank picnic excursion",
    "Sunset dolphin cruise",
    "Whale shark spotting tour",
    "Underwater dining (Ithaa)",
    "Speedboat island hopping",
    "Submarine ride Whale Submarine Maldives",
    "Resort day-pass (Kurumba / Bandos)",
    "Local island day trip Maafushi",
    "Big game fishing trip",
    "Surf trip North Male atoll",
    "Couples spa over the water",
  ],
  oman: [
    "Wahiba Sands desert safari + camping",
    "Wadi Shab hike + swim",
    "Mutrah corniche dhow cruise",
    "Bimmah Sinkhole swim",
    "Jebel Akhdar mountain tour",
    "Salalah frankincense trail",
    "Musandam fjords cruise",
    "Sur turtle reserve",
    "Royal Opera House Muscat tour",
    "Nizwa Fort + Friday goat market",
    "Wadi Bani Khalid",
  ],
  malaysia: [
    "Sunway Lagoon waterpark",
    "Genting SkyWorlds theme park",
    "KL Tower + Sky Deck",
    "Petronas Twin Towers Skybridge",
    "Penang Hill funicular + Habitat",
    "Langkawi Sky Bridge cable car",
    "Batu Caves day tour",
    "Cameron Highlands tea plantation tour",
    "Borneo orangutan sanctuary",
    "Mount Kinabalu trek",
    "Aquaria KLCC",
    "Legoland Malaysia",
    "Melaka heritage walking tour",
  ],
  singapore: [
    "Universal Studios Singapore",
    "Gardens by the Bay Cloud Forest",
    "Sentosa Skyline Luge",
    "Singapore Zoo + River Wonders",
    "Night Safari Singapore",
    "S.E.A. Aquarium",
    "Marina Bay Sands SkyPark",
    "Adventure Cove Waterpark",
    "Singapore Flyer",
    "Mandai Bird Paradise",
    "Sentosa cable car",
    "ArtScience Museum",
    "Wings of Time Sentosa show",
  ],
  "sri-lanka": [
    "Sigiriya Lion Rock fortress",
    "Ella nine-arch bridge train",
    "Yala safari (leopard tracking)",
    "Mirissa whale watching",
    "Galle Fort walking tour",
    "Adam's Peak sunrise trek",
    "Pinnawala elephant orphanage",
    "Kandy temple of the tooth",
    "Polonnaruwa ancient city",
    "Bentota water sports",
    "Trincomalee snorkeling",
    "Tea plantation tour Nuwara Eliya",
  ],
  nepal: [
    "Pokhara paragliding",
    "Chitwan jungle safari",
    "Everest helicopter day tour",
    "Annapurna day hike",
    "Sarangkot sunrise + bungee",
    "Bhaktapur durbar square tour",
    "Kathmandu Pashupatinath aarti",
    "Phewa Lake boating",
    "Ghorepani Poon Hill trek",
    "Lumbini Buddha pilgrimage",
    "White-water rafting Trishuli river",
  ],
  philippines: [
    "Boracay island hopping",
    "Chocolate Hills Bohol",
    "Palawan El Nido tour A/B/C",
    "Cebu canyoneering Kawasan falls",
    "Coron sunken WWII wrecks dive",
    "Siargao Cloud 9 surf lesson",
    "Bohol tarsier sanctuary + Loboc river cruise",
    "Hundred Islands Pangasinan",
    "Banaue rice terraces",
    "Manila Intramuros walking tour",
    "Donsol whale shark interaction",
  ],
  laos: [
    "Kuang Si Falls day trip",
    "Luang Prabang elephant sanctuary",
    "Vang Vieng kayak + tubing",
    "Pak Ou caves boat tour",
    "Plain of Jars Phonsavan",
    "Tad Lo waterfalls trek",
    "Buddha Park Vientiane",
    "Bolaven Plateau coffee plantation tour",
    "Si Phan Don (4000 islands) sunset cruise",
    "Hot-air balloon ride Vang Vieng",
  ],
};

export default function Activities() {
  const { country: slugParam } = useParams();
  const navigate = useNavigate();
  const [slug, setSlug] = useState(slugParam || "vietnam");
  const [query, setQuery] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [travelers, setTravelers] = useState(2);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);

  const country = COUNTRY_BY_SLUG[slug];
  const activityList = useMemo(
    () =>
      ACTIVITIES_BY_COUNTRY[slug] || ACTIVITIES_BY_COUNTRY.vietnam,
    [slug],
  );
  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activityList;
    return activityList.filter((a) => a.toLowerCase().includes(q));
  }, [query, activityList]);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    fetchCoupons().then((d) => setCoupons(d.items || [])).catch(() => {});
  }, []);

  // Sync slug to URL when changed via the dropdown
  useEffect(() => {
    if (slugParam && slugParam !== slug) {
      setSlug(slugParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugParam]);

  const onCountryChange = (newSlug) => {
    setSlug(newSlug);
    setResult(null);
    navigate(`/activities/${newSlug}`, { replace: true });
  };

  const runSearch = async (overrideQuery) => {
    const q = (overrideQuery ?? query).trim();
    if (q.length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    track("activity_search_started", { country: slug, query: q });
    setLoading(true);
    setResult(null);
    try {
      const data = await searchActivity({
        country_slug: slug,
        query: q,
        start_date: start || null,
        end_date: end || null,
        travelers: Number(travelers) || 2,
      });
      setResult(data);
      track("activity_search_succeeded", {
        country: slug,
        query: q,
        available_count: (data.offers || []).filter((o) => o.available).length,
      });
      // Scroll to results
      setTimeout(() => {
        document.getElementById("activity-results")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    } catch (e) {
      track("activity_search_failed", { country: slug, query: q });
      toast.error(e?.response?.data?.detail || "Could not search this activity. Try another phrase.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success(`Copied · ${code}`),
      () => toast.error("Couldn't copy — long-press to copy manually"),
    );
    track("coupon_copied", { code });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title={`Compare activity prices · Flycher${country ? " · " + country.name : ""}`}
        description="Compare ticket prices for a specific activity across Klook, GetYourGuide and Agoda — built for Indian travelers, in INR."
        path={slugParam ? `/activities/${slugParam}` : "/activities"}
      />
      <Navbar />

      <main className="flex-1 max-w-6xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        {/* Hero */}
        <span className="kicker">— Activity price compare</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          One activity. Three platforms.
          <br />
          <span className="italic" style={{ color: "#2A7FB8" }}>
            One sharper price.
          </span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          Search a specific experience — say <em>Vinwonders Nha Trang</em>, <em>Universal Studios Japan</em>, <em>Phi Phi island tour</em> — and we'll show ticket prices on <strong>Klook</strong>, <strong>GetYourGuide</strong> and <strong>Agoda</strong> side-by-side, with the latest curated coupons.
        </p>

        {/* Search form */}
        <div
          data-testid="activities-form"
          className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6"
        >
          <div className="grid sm:grid-cols-12 gap-4">
            <label className="sm:col-span-3 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Country</span>
              <select
                data-testid="activities-country"
                value={slug}
                onChange={(e) => onCountryChange(e.target.value)}
                className="input-base h-12 text-base"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="sm:col-span-5 flex flex-col gap-1.5 relative">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Activity</span>
              <div className="relative">
                <input
                  data-testid="activities-query"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSuggestOpen(true);
                  }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 180)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSuggestOpen(false);
                      runSearch();
                    } else if (e.key === "Escape") {
                      setSuggestOpen(false);
                    }
                  }}
                  placeholder={`Browse ${activityList.length}+ ${country?.name || ""} activities — type to filter`}
                  className="input-base h-12 text-base pr-10"
                  autoComplete="off"
                />
                <MagnifyingGlass
                  size={16}
                  weight="bold"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
              {suggestOpen && filteredSuggestions.length > 0 && (
                <div
                  data-testid="activities-suggest-dropdown"
                  className="absolute z-30 top-full left-0 right-0 mt-1.5 rounded-xl bg-white border border-slate-200 shadow-xl max-h-72 overflow-y-auto"
                >
                  <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                    Popular in {country?.name} · {filteredSuggestions.length} matches
                  </div>
                  {filteredSuggestions.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onMouseDown={(e) => {
                        // mousedown beats input blur so the click registers
                        e.preventDefault();
                        setQuery(sug);
                        setSuggestOpen(false);
                        runSearch(sug);
                      }}
                      data-testid={`activities-option-${sug.toLowerCase().replace(/\s+/g, "-").slice(0, 35)}`}
                      className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-[#EAF4FB] hover:text-[#2A7FB8] flex items-center justify-between gap-2"
                    >
                      <span>{sug}</span>
                      <Sparkle size={12} weight="fill" color="#FF7E4D" className="opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Start</span>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                data-testid="activities-start"
                className="input-base h-12 text-sm"
              />
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">End</span>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="activities-end"
                className="input-base h-12 text-sm"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 justify-between">
            <label className="flex items-center gap-2">
              <Users size={16} className="text-slate-400" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Travelers</span>
              <input
                type="number"
                min={1}
                max={20}
                value={travelers}
                onChange={(e) => setTravelers(e.target.value)}
                data-testid="activities-travelers"
                className="input-base h-10 w-20 text-sm text-center"
              />
            </label>
            <button
              type="button"
              onClick={() => runSearch()}
              disabled={loading || query.trim().length < 2}
              data-testid="activities-search-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-md shadow-sky-200/60 disabled:opacity-50"
              style={{ background: "#2A7FB8" }}
            >
              <MagnifyingGlass size={14} weight="bold" />
              {loading ? "Comparing prices…" : `Compare on 3 platforms`}
            </button>
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div data-testid="activities-loading" className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#2A7FB8] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-slate-600 font-mono uppercase tracking-wider">
              Pulling prices from Klook · GetYourGuide · Agoda…
            </p>
          </div>
        )}

        {result && !loading && (
          <section id="activity-results" className="mt-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <span className="kicker">— Results</span>
                <h2 className="font-serif text-3xl sm:text-4xl mt-1 text-slate-900">
                  {result.query}
                </h2>
                <p className="text-slate-500 mt-1 text-sm font-mono">{result.country}</p>
              </div>
              <div className="text-xs text-slate-500 font-mono flex items-center gap-1">
                <Calendar size={12} />
                {start || "flexible"} → {end || "flexible"} · {travelers} {Number(travelers) === 1 ? "adult" : "adults"}
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="activities-offers">
              {result.offers.map((o) => (
                <OfferCard key={o.platform} offer={o} onCopyCode={copyCode} />
              ))}
            </div>

            {result.tips?.length > 0 && (
              <div className="mt-8 rounded-2xl bg-[#EAF4FB] border border-[#2A7FB8]/15 p-5">
                <span className="kicker">— Smart booking tips</span>
                <ul className="mt-3 space-y-2">
                  {result.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Sparkle size={14} className="mt-1 flex-shrink-0" color="#FF7E4D" weight="fill" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Standalone coupon strip */}
        {coupons.length > 0 && (
          <section className="mt-16" data-testid="coupons-section">
            <span className="kicker">— Active coupons</span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-1 text-slate-900">
              This week's hand-picked codes
            </h2>
            <p className="text-slate-600 mt-2 text-sm max-w-xl">
              Auto-refreshed every week — codes evolve as platforms rotate promos.
            </p>
            <div className="mt-6 grid md:grid-cols-3 gap-4">
              {coupons.map((c) => (
                <article
                  key={c.platform}
                  data-testid={`coupon-${c.platform}`}
                  className="rounded-2xl bg-white border border-slate-200 p-5 flex flex-col gap-3"
                >
                  <div className="flex items-center gap-3">
                    {c.favicon && (
                      <img
                        src={c.favicon}
                        alt=""
                        className="w-7 h-7 rounded-md"
                        referrerPolicy="no-referrer"
                      />
                    )}
                    <div className="font-semibold text-slate-900">{c.platform_name}</div>
                    {c.source === "grabon.in" && (
                      <span
                        className="ml-auto text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                        title="Auto-verified this week"
                      >
                        Live
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyCode(c.code)}
                    data-testid={`coupon-copy-${c.platform}`}
                    className="group inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[#2A7FB8]/40 hover:border-[#2A7FB8] hover:bg-[#EAF4FB] transition-colors"
                  >
                    <span className="font-mono font-bold text-base tracking-wider text-[#2A7FB8]">
                      {c.code}
                    </span>
                    <Copy size={14} weight="bold" className="text-slate-400 group-hover:text-[#2A7FB8]" />
                  </button>
                  {c.note && <p className="text-xs text-slate-600 leading-relaxed">{c.note}</p>}
                  <div className="mt-auto pt-2 text-[10px] text-slate-400 font-mono">
                    {c.expires_at ? `Valid till ${c.expires_at}` : "Evergreen offer"}
                    {c.updated_at && (
                      <> · Refreshed {new Date(c.updated_at).toLocaleDateString()}</>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function OfferCard({ offer, onCopyCode }) {
  const isAvailable = offer.available;
  return (
    <article
      data-testid={`offer-${offer.platform}`}
      className={`rounded-2xl bg-white border ${
        isAvailable ? "border-slate-200 hover:border-[#2A7FB8]" : "border-slate-100"
      } p-5 flex flex-col transition-colors`}
    >
      <div className="flex items-center gap-3">
        <img
          src={offer.favicon}
          alt=""
          className="w-9 h-9 rounded-lg"
          referrerPolicy="no-referrer"
        />
        <div>
          <div className="font-semibold text-slate-900">{offer.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
            Activity ticket
          </div>
        </div>
      </div>

      <div className="mt-4">
        {isAvailable ? (
          <>
            <div className="text-2xl font-serif text-slate-900" data-testid={`offer-price-${offer.platform}`}>
              {offer.price_inr || "Price on platform"}
            </div>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">{offer.note}</p>
          </>
        ) : (
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600 flex items-start gap-2">
            <WarningCircle size={16} weight="duotone" color="#94A3B8" className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-slate-700">Not available on {offer.name}</div>
              <p className="text-xs mt-1">{offer.note}</p>
            </div>
          </div>
        )}
      </div>

      {/* Coupon */}
      {isAvailable && offer.coupon_code && (
        <button
          type="button"
          onClick={() => onCopyCode(offer.coupon_code)}
          data-testid={`offer-coupon-${offer.platform}`}
          className="mt-4 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-slate-200 hover:border-[#FF7E4D] hover:bg-[#FFF1E8] group transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Tag size={14} weight="duotone" color="#FF7E4D" />
            <div>
              <div className="font-mono font-bold text-sm tracking-wider text-slate-900">
                {offer.coupon_code}
              </div>
              {offer.coupon_note && (
                <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1 max-w-[200px]">
                  {offer.coupon_note}
                </div>
              )}
            </div>
          </div>
          <Copy size={12} weight="bold" className="text-slate-400 group-hover:text-[#FF7E4D]" />
        </button>
      )}

      {/* CTA */}
      {isAvailable && (
        <a
          href={offer.deeplink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`offer-cta-${offer.platform}`}
          className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold shadow-sm text-white"
          style={{ background: offer.color }}
        >
          View on {offer.name} <ArrowSquareOut size={14} weight="bold" />
        </a>
      )}

      {!isAvailable && (
        <a
          href={offer.deeplink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`offer-cta-${offer.platform}`}
          className="mt-4 text-center text-[11px] text-slate-500 underline-offset-4 hover:underline"
        >
          Try searching on {offer.name} anyway →
        </a>
      )}
    </article>
  );
}
